<template>
  <div class="gen-attendance" :class="{ 'is-compact-upload': compactUpload, 'is-upload-only': uploadOnly, 'is-table-only': tableOnly }">
    <!-- ============ 上传考试原始数据（还原原样式）+ 下方考试标签（每次上传即新增一条） ============ -->
    <!-- table-only 模式（步骤页整合表复用）不渲染上传区 -->
    <div v-if="!tableOnly" class="gen-att-upload-card">
      <div class="gen-att-upload-header">
        <div class="gen-att-upload-title">
          <i class="el-icon-upload2"></i>
          <span>上传考试原始数据</span><span class="gen-att-upload-title-tip">(每次上传新增一条考试数据)</span>
        </div>
        <span class="gen-att-tip">支持 XLS / XLSX 格式，选择后自动上传并整合</span>
      </div>
      <div class="gen-att-upload-body">
        <el-upload
          ref="uploadRef"
          class="gen-att-upload"
          drag
          action="#"
          :auto-upload="false"
          :limit="1"
          accept=".xls,.xlsx"
          :file-list="fileList"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :on-exceed="handleFileExceed"
          :before-upload="() => false"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将考试表原始数据拖到此处，或<em>点击选择文件</em></div>
        </el-upload>
        <span v-if="uploadLoading" class="gen-att-upload-status">
          <i class="el-icon-loading"></i> 正在上传整合中...
        </span>
      </div>
      <!-- 状态卡片（还原原样式：米黄色背景，展示上传状态） -->
      <div class="gen-att-upload-status-card">
        <div class="gen-att-upload-status-card__main">{{ attendanceUploadStatus }}</div>
        <div class="gen-att-upload-status-card__sub">{{ attendanceUploadHint }}</div>
      </div>

      <!-- 考试原始数据标签：状态卡片下方，每条一个可点击标签，点击打开详情弹框；标签内含单条删除键 -->
      <div v-if="hasMergeRecord" class="gen-att-exam-tags">
        <template v-if="examList.length">
          <span
            v-for="(exam, idx) in examList"
            :key="exam.rrmusmExamId"
            class="gen-att-exam-tag"
            :class="{ 'is-active': currentExam && currentExam.rrmusmExamId === exam.rrmusmExamId }"
            @click="openExamDetail(exam)"
          >
            <i class="el-icon-document"></i>
            <span class="gen-att-exam-tag-name">考试{{ idx + 1 }}</span>
            <span class="gen-att-exam-tag-time">{{ formatExamTime(exam.createtime) }}</span>
            <i class="el-icon-close gen-att-exam-tag-del" title="删除此考试" @click.stop="handleDeleteExam(exam, idx)"></i>
          </span>
        </template>
        <span v-else class="gen-att-exam-tags-empty">暂无考试原始数据，上传后将显示为标签，点击可查看完整数据</span>
      </div>
      <div v-else-if="!loading" class="gen-att-exam-tags">
        <span class="gen-att-exam-tags-empty">请先在「签到表」模块完成签到识别与整合</span>
      </div>
    </div>

    <!-- ============ 考试原始数据详情弹框（复用封装的拖拽+拉伸指令） ============ -->
    <el-dialog
      v-dialog-drag-resize
      :visible.sync="detailVisible"
      :title="detailTitle"
      width="80%"
      :show-close="true"
      append-to-body
      custom-class="gen-att-detail-dialog"
      @close="currentExam = null"
    >
      <div v-if="currentExam" class="gen-att-detail-body">
        <div class="gen-att-detail-ops">
          <el-upload
            class="gen-att-detail-upload"
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            accept=".xls,.xlsx"
            :on-change="(file) => handleUpdateExamFile(file, currentExam)"
          >
            <el-button size="small" icon="el-icon-refresh-left" :loading="currentExam._uploading">重新上传此考试</el-button>
          </el-upload>
          <span class="gen-att-detail-tip">覆盖当前这条考试的原始数据（上传后会自动重新整合）</span>
        </div>
        <div v-if="getExamRows(currentExam).length" class="gen-att-detail-table">
          <el-table :data="getExamRows(currentExam)" border stripe size="small" :max-height="560" cell-class-name="att-cell-center">
            <el-table-column
              v-for="col in getExamColumns(currentExam)"
              :key="col.prop"
              :prop="col.prop"
              :label="col.label"
              :min-width="col.minWidth"
              align="center"
              header-align="center"
              show-overflow-tooltip
            />
          </el-table>
        </div>
        <el-empty v-else description="该考试暂无可展示的原始数据" />
      </div>
    </el-dialog>

    <!-- ============ 下方：签到+考试整合数据表格（共用，所有考试合并后的最终结果） ============ -->
    <!-- exam-only 模式下不渲染整合表（由外部统一展示） -->
    <div v-if="!uploadOnly && !examOnly" v-loading="loading" class="gen-att-table-card">
      <div class="gen-att-table-header">
        <div class="gen-att-table-title">
          <i class="el-icon-s-grid"></i>
          <span>签到+考试整合数据</span>
          <span v-if="tableRows.length" class="gen-att-table-count">{{ tableRows.length }} 条</span>
        </div>
        <div v-if="latestRecord" class="gen-att-table-actions">
          <span class="gen-att-table-meta">{{ latestRecord.createusername || '-' }} · {{ latestRecord.createtime || '-' }}</span>
          <template v-if="tableRows.length">
            <template v-if="!isEditing">
              <el-button size="small" icon="el-icon-edit" @click="handleStartEdit">编辑</el-button>
              <el-button type="success" size="small" icon="el-icon-download" :loading="exportModelLoading" @click="handleExport">{{ hasExamModel ? '使用模板导出' : '导出 Excel' }}</el-button>
              <el-button type="warning" size="small" icon="el-icon-delete" :loading="deleteLoading" @click="handleDeleteSignAddExam">清空考勤数据</el-button>
            </template>
            <template v-else>
              <el-button size="small" @click="handleCancelEdit">取消</el-button>
              <el-button type="primary" size="small" :loading="editLoading" @click="handleConfirmEdit">保存</el-button>
            </template>
          </template>
        </div>
      </div>
      <div class="gen-att-table-body">
        <!-- 空状态 -->
        <div v-if="!loading && tableRows.length === 0" class="gen-att-empty">
          <div class="gen-att-empty-icon"><i class="el-icon-date"></i></div>
          <div class="gen-att-empty-text">暂无整合数据</div>
          <div class="gen-att-empty-hint">上传考试成绩表后，签到+考试整合数据将显示在这里</div>
        </div>
        <!-- 表格 -->
        <div v-if="tableRows.length" class="gen-att-table-wrap">
          <el-table
            :data="isEditing ? editableRows : tableRows"
            border
            stripe
            size="small"
            max-height="460"
            :cell-class-name="mergeTableCellClass"
            :class="{ 'is-cell-editing': isEditing }"
          >
            <el-table-column
              v-for="(col, ci) in columnConfigs"
              :key="col.prop"
              :prop="col.prop"
              :label="col.label"
              :min-width="col.minWidth"
              align="center"
              header-align="center"
              :show-overflow-tooltip="!isEditing"
            >
              <template #default="{ row, $index }">
                <template v-if="isEditing">
                  <div
                    v-if="editingCell && editingCell.rowIdx === $index && editingCell.colIdx === ci"
                    class="att-cell-editing"
                  >
                    <el-input
                      v-model="editableRows[$index][col.prop]"
                      size="mini"
                      class="att-cell-input"
                      @blur="editingCell = null"
                      @keydown.enter.native="editingCell = null"
                    />
                  </div>
                  <div v-else class="att-cell-text" @click="startCellEdit($index, ci)">{{ row[col.prop] || '' }}</div>
                </template>
                <span v-else>{{ row[col.prop] || '' }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  addRtReportModelUserSignMergeExam,
  updateRtReportModelUserSignMergeExam,
  deleteRtReportModelUserSignMergeExam,
  getRtReportModelUserSignMergeList,
  exportRtReportModelUserSignMergeSignExam,
  exportRtReportModelUserSignMergeSignExamUseModel,
  updateRtReportModelUserSignMergeSignUser,
  deleteRtReportModelUserSignMergeSignAddExam
} from '@/api/api'

// 解析表格类 JSON（signAddExam / examUp 通用，兼容对象和 JSON 字符串，修复后端数字 key 无引号的问题）
function parseMergeJson (val) {
  if (!val) return { columns: [], rows: [] }
  try {
    // 如果已经是对象直接用，否则解析字符串
    let data
    if (typeof val !== 'string') {
      data = val
    } else {
      // 后端返回的 JSON 中数字 key 无引号（如 {0:"序号"}），需修复为 {"0":"序号"} 再 parse
      const fixed = val.replace(/([{,]\s*)(\d+)\s*:/g, '$1"$2":')
      data = JSON.parse(fixed)
    }

    // 格式1: {headerMap: [["序号"],["姓名"],...], rowMapList: [["1","胡良",...],...]}
    if (data.headerMap && data.rowMapList) {
      const indices = data.headerMap
        .map((h, i) => (h[0] && String(h[0]).trim()) ? i : -1)
        .filter(i => i >= 0)
      if (!indices.length) return { columns: [], rows: [] }
      const columns = indices.map(i => data.headerMap[i][0] || '')
      const rows = data.rowMapList.map(row => {
        const obj = {}
        indices.forEach((colIdx, i) => { obj['col_' + i] = row[colIdx] || '' })
        return obj
      }).filter(r => Object.values(r).some(v => v !== ''))
      return { columns, rows }
    }

    // 格式2: {headList: [{0:"序号",1:"姓名",...}], dataList: [{0:"1",1:"胡良",...}]}
    if (data.headList && data.dataList) {
      const headList = data.headList || []
      const dataList = data.dataList || []
      if (!headList.length) return { columns: [], rows: [] }
      const headerObj = headList[0] || {}
      const colKeys = Object.keys(headerObj).sort((a, b) => Number(a) - Number(b))
      const columns = colKeys.map(k => headerObj[k] || '').filter(h => h)
      const colIndices = colKeys.filter(k => headerObj[k] && String(headerObj[k]).trim())
      const rows = dataList.map(row => {
        const obj = {}
        colIndices.forEach((colIdx, i) => { obj['col_' + i] = row[colIdx] || '' })
        return obj
      }).filter(r => Object.values(r).some(v => v !== ''))
      return { columns, rows }
    }

    return { columns: [], rows: [] }
  } catch {
    return { columns: [], rows: [] }
  }
}

// 根据表头字数动态计算列宽（上限 400px）
function buildColumnConfigs (columns) {
  return columns.map((col, i) => {
    const text = String(col || '').trim()
    let charWidth = 0
    for (const ch of text) {
      charWidth += /[一-鿿]/.test(ch) ? 16 : 10
    }
    const minWidth = Math.min(Math.max(80, charWidth + 64), 400)
    return { prop: 'col_' + i, label: col, minWidth }
  })
}

// 判断列头是否为「日期/时段」类型：3日、三日、3日上午、3日下午、10日 等
function isDateColumnHeader (label) {
  if (!label) return false
  const s = String(label).trim()
  // 数字或中文数字 + 日 + 可选(上午|下午|晚上|全天)
  return /^(\d{1,2}|[一二三四五六七八九十]{1,3})\s*日\s*(上午|下午|晚|晚上|全天)?$/.test(s)
}

// 判断单元格值是否为正常签到值：OK / 请假（忽略大小写、全半角、空格）
function isNormalSignInValue (val) {
  if (!val) return false
  const s = String(val).trim().toLowerCase().replace(/\s+/g, '')
  // 全角字母转半角
  const fullWidthMap = { ｏ: 'o', ｋ: 'k' }
  const norm = s.replace(/[ｏｋ]/g, c => fullWidthMap[c] || c)
  if (norm === 'ok' || norm === 'o' || norm === '√' || norm === '✓') return true
  if (/^请\s*假$/.test(s) || s === '请假' || s === '缺勤' || s === '缺席') return true
  return false
}

export default {
  name: 'ReportGenAttendance',
  props: {
    rrmaId: { type: [Number, String], default: null },
    compactUpload: { type: Boolean, default: false },
    uploadOnly: { type: Boolean, default: false },
    tableOnly: { type: Boolean, default: false },
    // 仅显示考试多条区，不显示下方整合表（步骤页场景：整合表由外部统一展示）
    examOnly: { type: Boolean, default: false },
    mergeRecord: { type: Object, default: null }
  },
  data () {
    return {
      loading: false,
      uploadLoading: false,
      exportModelLoading: false,
      editLoading: false,
      deleteLoading: false,
      fileList: [],
      // 整合主表记录（含 signAddExam / rrmusmId 等）
      latestRecord: null,
      // 下方共用整合表数据（所有考试合并后的最终结果）
      tableColumns: [],
      tableRows: [],
      columnConfigs: [],
      isEditing: false,
      editableRows: [],
      editingCell: null,
      // ===== 考试原始数据多条（标签 + 详情弹框） =====
      // 多条考试记录列表（rtReportModelUserSignMergeExamList）
      examList: [],
      // 单条考试 examUp 解析缓存（key=rrmusmExamId），避免重复解析
      examParsedCache: {},
      // 详情弹框：是否显示 + 当前查看的考试记录
      detailVisible: false,
      currentExam: null
    }
  },
  computed: {
    // 是否已有整合主表记录（rrmusmId 存在才允许新增/编辑考试）
    hasMergeRecord () {
      return !!(this.latestRecord && this.latestRecord.rrmusmId)
    },
    hasExamModel () {
      const record = this.latestRecord
      return record && Number(record.signExamModel) === 2
    },
    // 上传状态卡片主文案
    attendanceUploadStatus () {
      if (this.uploadLoading) return '正在上传并整合考试原始数据'
      if (this.examList.length) return `已上传 ${this.examList.length} 份考试原始数据`
      return '待上传考试原始数据'
    },
    // 上传状态卡片副文案
    attendanceUploadHint () {
      if (this.uploadLoading) return '请等待上传完成，系统会自动刷新整合数据。'
      if (this.examList.length) return '每次上传都会新增一条考试记录，下方标签可点击查看完整数据。'
      return '支持 XLS / XLSX 格式，每次上传新增一条考试记录并自动整合。'
    },
    // 详情弹框标题
    detailTitle () {
      if (!this.currentExam) return '考试原始数据'
      const idx = this.examList.findIndex(e => e.rrmusmExamId === this.currentExam.rrmusmExamId)
      return idx >= 0 ? `考试${idx + 1} 原始数据` : '考试原始数据'
    }
  },
  watch: {
    rrmaId: {
      handler (val) {
        // exam-only / table-only 由父组件统一拉取记录并通过 mergeRecord 传入，避免重复请求同一接口
        if (val && !this.uploadOnly && !this.examOnly && !this.tableOnly) this.fetchList()
      },
      immediate: true
    },
    mergeRecord: {
      handler (record) {
        // table-only 模式：直接用父组件传入的主表记录渲染下方整合表
        if (this.tableOnly && record) this.applyMergeRecord(record)
        // exam-only 模式：从父组件统一拉取的主表记录中提取考试列表，无需自己再请求
        if (this.examOnly && record) this.applyExamList(record)
        // upload-only 模式：仅同步 latestRecord 以获取 rrmusmId（供新增考试用），不渲染整合表
        if (this.uploadOnly) this.latestRecord = record || null
      },
      immediate: true
    }
  },
  methods: {
    // 应用整合主表记录 → 解析下方共用整合表（signAddExam）
    applyMergeRecord (record) {
      this.latestRecord = record || null
      const parsed = parseMergeJson(record && record.signAddExam)
      this.tableColumns = parsed.columns
      this.tableRows = parsed.rows
      this.columnConfigs = buildColumnConfigs(parsed.columns)
      this.handleCancelEdit()
    },
    // 从主表记录中提取多条考试子记录（exam-only 模式：由父组件统一拉取后传入，避免重复请求）
    applyExamList (record) {
      this.latestRecord = record || null
      const list = (record && record.rtReportModelUserSignMergeExamList) || []
      this.examList = list.map(item => ({ ...item, _uploading: false }))
      this.examParsedCache = {}
      // 若弹框正打开，同步刷新 currentExam
      if (this.currentExam) {
        const fresh = this.examList.find(e => e.rrmusmExamId === this.currentExam.rrmusmExamId)
        this.currentExam = fresh || null
        if (!fresh) this.detailVisible = false
      }
    },

    // 查询整合记录：取主对象，解析下方共用整合表 + 上方多条考试列表
    async fetchList () {
      if (!this.rrmaId) return
      this.loading = true
      try {
        const res = await getRtReportModelUserSignMergeList({ rrmaId: this.rrmaId })
        // 兼容 data 为单条对象或数组（一个申请只有一条整合主记录）
        let record = null
        if (Array.isArray(res.data)) {
          record = res.data.length ? res.data[res.data.length - 1] : null
        } else if (res.data && res.data.rrmusmId) {
          record = res.data
        }
        this.applyMergeRecord(record)
        // 解析多条考试成绩子记录
        const list = (record && record.rtReportModelUserSignMergeExamList) || []
        this.examList = list.map(item => ({ ...item, _uploading: false }))
        this.examParsedCache = {}
        // 若弹框正打开，同步刷新 currentExam（重新上传后数据已变化）
        if (this.currentExam) {
          const fresh = this.examList.find(e => e.rrmusmExamId === this.currentExam.rrmusmExamId)
          this.currentExam = fresh || null
          if (!fresh) this.detailVisible = false
        }
      } catch (e) {
        this.latestRecord = null
        this.tableColumns = []
        this.tableRows = []
        this.columnConfigs = []
        this.examList = []
        this.examParsedCache = {}
        this.handleCancelEdit()
      } finally {
        this.loading = false
      }
    },

    // 打开考试详情弹框
    openExamDetail (exam) {
      this.currentExam = exam
      this.detailVisible = true
    },

    // 整合表单元格样式：日期列下，非「OK / 请假 / 本人签到」的异常值标黄
    // 规则：列头为日期格式（如 3日/3日上午/三日/3日下午）时，单元格值若既不是 ok/请假，
    //      也不等于该行姓名列的值（即非本人签到），则标记为异常（代签等场景）
    mergeTableCellClass ({ column, row }) {
      // 非日期列不处理；编辑态不染色（避免干扰输入）
      if (this.isEditing) return 'att-cell-center'
      const label = column && column.label ? String(column.label).trim() : ''
      if (!isDateColumnHeader(label)) return 'att-cell-center'
      const cellVal = row && column.property ? String(row[column.property] || '').trim() : ''
      // 空值不标黄（如请假人当天本就空）
      if (!cellVal) return 'att-cell-center'
      // 正常值：ok（忽略大小写/全角半角）、请假
      if (isNormalSignInValue(cellVal)) return 'att-cell-center'
      // 等于本人姓名 → 本人签到，不处理
      const nameProp = this.getNameProp()
      if (nameProp && row[nameProp] && String(row[nameProp]).trim() === cellVal) return 'att-cell-center'
      // 其余视为异常（如代签的他人姓名）
      return 'att-cell-center att-cell-warn'
    },

    // 缓存姓名列的 prop（label 含「姓名」）
    getNameProp () {
      if (this._namePropCacheKey !== this.columnConfigs.length) {
        const hit = this.columnConfigs.find(c => /姓名/.test(c.label || ''))
        this._namePropCacheKey = this.columnConfigs.length
        this._namePropCache = hit ? hit.prop : ''
      }
      return this._namePropCache
    },

    // 格式化考试时间（截断秒，只显示到分钟）
    formatExamTime (t) {
      if (!t) return ''
      return String(t).replace(/:\d{2}$/, '').replace('T', ' ')
    },

    // 解析单条考试的 examUp 原始数据（带缓存）
    getExamParsed (exam) {
      if (!exam || !exam.rrmusmExamId) return { columns: [], rows: [] }
      const id = exam.rrmusmExamId
      if (!this.examParsedCache[id]) {
        this.$set(this.examParsedCache, id, parseMergeJson(exam.examUp))
      }
      return this.examParsedCache[id]
    },
    getExamColumns (exam) {
      return buildColumnConfigs(this.getExamParsed(exam).columns)
    },
    getExamRows (exam) {
      return this.getExamParsed(exam).rows
    },

    // ===== 轻量刷新：仅重新拉取主表整合数据（signAddExam），不重置考试列表、不触发签到OCR =====
    // 考试新增/编辑后，后端会重新合并所有考试写入主表 signAddExam，这里只同步下方共用整合表
    // upload-only / exam-only 模式下本组件不渲染整合表，无需刷新（交由父组件统一处理）
    async refreshMergeOnly () {
      if (!this.rrmaId || this.uploadOnly || this.examOnly) return
      try {
        const res = await getRtReportModelUserSignMergeList({ rrmaId: this.rrmaId })
        let record = null
        if (Array.isArray(res.data)) {
          record = res.data.length ? res.data[res.data.length - 1] : null
        } else if (res.data && res.data.rrmusmId) {
          record = res.data
        }
        // 只更新整合表相关字段，保留 examList 不动（避免覆盖已就地更新的数据）
        if (record) {
          this.latestRecord = { ...this.latestRecord, ...record }
          const parsed = parseMergeJson(record.signAddExam)
          this.tableColumns = parsed.columns
          this.tableRows = parsed.rows
          this.columnConfigs = buildColumnConfigs(parsed.columns)
          this.handleCancelEdit()
        }
      } catch (e) {
        /* 忽略：整合表刷新失败不影响考试列表已更新状态 */
      }
    },

    // 就地把新增/编辑返回的考试记录合并进 examList（避免全量 fetchList）
    upsertExamRecord (examRecord) {
      if (!examRecord || !examRecord.rrmusmExamId) return
      const idx = this.examList.findIndex(e => e.rrmusmExamId === examRecord.rrmusmExamId)
      const normalized = { ...examRecord, _uploading: false }
      if (idx >= 0) {
        // 编辑：替换该条，并清掉它的解析缓存
        this.$set(this.examList, idx, normalized)
        this.$delete(this.examParsedCache, examRecord.rrmusmExamId)
      } else {
        // 新增：追加到末尾
        this.examList.push(normalized)
      }
    },

    // ===== 重新上传单条考试（覆盖该条 examUp） =====
    async handleUpdateExamFile (file, exam) {
      if (!file || !file.raw || !exam || !exam.rrmusmExamId) return
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!['.xls', '.xlsx'].includes(ext)) {
        this.$message.warning('仅支持 XLS、XLSX 格式的 Excel 文件')
        return
      }
      this.$set(exam, '_uploading', true)
      this.$emit('busy-change', true)
      try {
        // 编辑单条考试用 rrmusmExamId；接口返回更新后的考试记录对象
        const res = await updateRtReportModelUserSignMergeExam({ rrmusmExamId: exam.rrmusmExamId, file: file.raw })
        this.$message.success('该考试已更新，已重新整合')
        // 就地更新该条考试记录（含清缓存）
        const updated = res && res.data
        if (updated) this.upsertExamRecord(updated)
        // 仅轻量刷新下方共用整合表
        await this.refreshMergeOnly()
        this.$emit('merge-updated', { source: 'exam' })
      } catch (err) {
        this.$message.error(err.message || '更新考试失败，请稍后重试')
      } finally {
        this.$set(exam, '_uploading', false)
        this.$emit('busy-change', false)
      }
    },

    // ===== 删除单条考试（tab 上的删除键） =====
    async handleDeleteExam (exam, idx) {
      if (!exam || !exam.rrmusmExamId) return
      try {
        await this.$confirm(`确认删除「考试${idx + 1}」？删除后将重新整合剩余考试数据。`, '删除考试', { type: 'warning' })
      } catch (e) {
        return // 用户取消
      }
      this.$emit('busy-change', true)
      try {
        await deleteRtReportModelUserSignMergeExam({ rrmusmExamId: exam.rrmusmExamId })
        this.$message.success('删除成功')
        // 就地从列表移除该条 + 清缓存
        const pos = this.examList.findIndex(e => e.rrmusmExamId === exam.rrmusmExamId)
        if (pos >= 0) this.examList.splice(pos, 1)
        this.$delete(this.examParsedCache, exam.rrmusmExamId)
        // 若详情弹框正显示该条，关闭它
        if (this.currentExam && this.currentExam.rrmusmExamId === exam.rrmusmExamId) {
          this.detailVisible = false
          this.currentExam = null
        }
        // 轻量刷新下方共用整合表（后端已重新合并剩余考试）
        await this.refreshMergeOnly()
        this.$emit('merge-updated', { source: 'exam' })
      } catch (err) {
        this.$message.error(err.message || '删除失败，请稍后重试')
      } finally {
        this.$emit('busy-change', false)
      }
    },

    // ===== upload-only 模式：上传即新增一条考试 =====
    // 上传卡片：选择文件后自动新增一条考试记录（覆盖上传卡片场景：主弹框 / upload-only）
    async handleFileChange (file) {
      if (!file || !file.raw) return
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!['.xls', '.xlsx'].includes(ext)) {
        this.$message.warning('仅支持 XLS、XLSX 格式的 Excel 文件')
        this.handleFileRemove()
        return
      }
      if (!this.hasMergeRecord) {
        this.$message.warning('暂无考勤整合记录，请先完成签到整合')
        this.handleFileRemove()
        return
      }
      this.uploadLoading = true
      this.$emit('busy-change', true)
      try {
        const res = await addRtReportModelUserSignMergeExam({ rrmusmId: this.latestRecord.rrmusmId, file: file.raw })
        this.$message.success('考试记录新增成功，已重新整合')
        // 就地追加返回的考试记录到标签条
        const newRecord = res && res.data
        if (newRecord) this.upsertExamRecord(newRecord)
        // 仅轻量刷新整合表（exam-only 模式下不渲染整合表，refreshMergeOnly 内部会跳过）
        await this.refreshMergeOnly()
        // 通知父组件：来源为考试变更，无需刷新签到OCR
        this.$emit('merge-updated', { source: 'exam' })
      } catch (err) {
        this.$message.error(err.message || '上传整合失败，请稍后重试')
      } finally {
        this.uploadLoading = false
        this.$emit('busy-change', false)
        this.handleFileRemove()
      }
    },
    handleFileRemove () {
      this.fileList = []
      if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
    },
    handleFileExceed () {
      this.$message.warning('请先完成当前文件上传')
    },

    // ===== 下方共用整合表的编辑/保存（沿用原逻辑） =====
    handleStartEdit () {
      this.editableRows = JSON.parse(JSON.stringify(this.tableRows))
      this.editingCell = null
      this.isEditing = true
    },
    handleCancelEdit () {
      this.editableRows = []
      this.editingCell = null
      this.isEditing = false
    },
    startCellEdit (rowIdx, colIdx) {
      this.editingCell = { rowIdx, colIdx }
      this.$nextTick(() => {
        const input = this.$el.querySelector('.att-cell-editing .el-input__inner')
        if (input) input.focus()
      })
    },
    buildSignAddExamJson () {
      const headerObj = {}
      this.tableColumns.forEach((header, index) => { headerObj[index] = header || '' })
      const dataList = this.editableRows.map(row => {
        const rowObj = {}
        this.tableColumns.forEach((_, index) => { rowObj[index] = row['col_' + index] || '' })
        return rowObj
      })
      return JSON.stringify({ headList: [headerObj], dataList })
    },
    async handleConfirmEdit () {
      if (!this.rrmaId || !this.latestRecord) return
      this.editLoading = true
      this.$emit('busy-change', true)
      try {
        await updateRtReportModelUserSignMergeSignUser({
          rrmaId: this.rrmaId,
          signAddExam: this.buildSignAddExamJson()
        })
        this.$message.success('整合数据已保存')
        this.isEditing = false
        this.editingCell = null
        // 编辑整合表只改 signAddExam，用轻量刷新即可（不重置考试列表、不触发OCR）
        await this.refreshMergeOnly()
        this.$emit('merge-updated', { source: 'sign' })
      } catch (e) {
        this.$message.error(e.message || '保存失败，请稍后重试')
      } finally {
        this.editLoading = false
        this.$emit('busy-change', false)
      }
    },
    async handleDeleteSignAddExam () {
      if (!this.latestRecord || !this.latestRecord.rrmusmId) {
        this.$message.warning('暂无可清空的考勤数据')
        return
      }
      try {
        await this.$confirm('确认清空签到+考试整合数据？将清空所有考试成绩记录，评估和报告数据不会被清空。', '清空考勤数据', { type: 'warning' })
        this.deleteLoading = true
        this.$emit('busy-change', true)
        await deleteRtReportModelUserSignMergeSignAddExam({ rrmusmId: this.latestRecord.rrmusmId })
        this.$message.success('考勤数据已清空')
        // 清空操作删除了全部考试子记录，需全量刷新
        await this.fetchList()
        this.$emit('merge-updated', { source: 'exam' })
      } catch (e) {
        if (e !== 'cancel') this.$message.error(e.message || '清空失败，请稍后重试')
      } finally {
        this.deleteLoading = false
        this.$emit('busy-change', false)
      }
    },

    // 根据 signExamModel 判断使用模板导出还是普通导出（导出整合主表合并结果）
    async handleExport () {
      if (!this.latestRecord || !this.latestRecord.rrmusmId) {
        this.$message.warning('暂无可导出的整合数据')
        return
      }
      this.exportModelLoading = true
      try {
        const params = { rrmusmId: this.latestRecord.rrmusmId }
        const result = this.hasExamModel
          ? await exportRtReportModelUserSignMergeSignExamUseModel(params)
          : await exportRtReportModelUserSignMergeSignExam(params)
        this.$message.success('导出成功：' + result.fileName)
      } catch (e) {
        this.$message.error(e.message || '导出失败，请稍后重试')
      } finally {
        this.exportModelLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// 考试份数计数标签（显示在上传卡片标题旁）
.gen-att-exam-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 4px;
  background: #2563eb;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
}

// ===== 考试原始数据标签条 =====
.gen-att-exam-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px 14px;
  border-top: 1px dashed #e8ecf1;
  background: linear-gradient(180deg, #ffffff, #fbfcfe);
}

.gen-att-exam-tags-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;

  i { color: #64748b; font-size: 14px; }
  em { font-style: normal; color: #2563eb; font-weight: 700; margin: 0 2px; }
}

// 单个考试标签（可点击 → 打开详情弹框）
.gen-att-exam-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  font-size: 12px;
  color: #1e40af;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.18s ease;
  user-select: none;

  i { font-size: 13px; color: #2563eb; }

  &:hover {
    color: #fff;
    background: #2563eb;
    border-color: #2563eb;
    box-shadow: 0 3px 8px rgba(37, 99, 235, 0.25);
    transform: translateY(-1px);
    i { color: #fff; }
    .gen-att-exam-tag-time { color: rgba(255, 255, 255, 0.85); }
  }

  // 当前选中（正在查看详情）的标签：深蓝填充 + 左侧高亮条
  &.is-active {
    color: #fff;
    background: linear-gradient(135deg, #1e40af, #1d4ed8);
    border-color: #1e40af;
    box-shadow: 0 4px 12px rgba(30, 64, 175, 0.35);
    i { color: #fff; }
    .gen-att-exam-tag-time { color: rgba(255, 255, 255, 0.85); }
  }
}

.gen-att-exam-tag-name {
  font-weight: 600;
}

.gen-att-exam-tag-time {
  font-size: 11px;
  color: #93a4c4;
  font-weight: 400;
  transition: color 0.18s ease;
}

// 标签上的单条删除键（小圆叉，hover 标签时显现红底白字）
.gen-att-exam-tag-del {
  margin-left: 2px;
  font-size: 13px;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  color: #94a3b8;
  transition: all 0.15s ease;

  &:hover {
    color: #fff;
    background: #ef4444;
  }
}

// 标签条空占位
.gen-att-exam-tags-empty {
  padding: 10px 16px 14px;
  font-size: 12px;
  color: #94a3b8;
  border-top: 1px dashed #e8ecf1;
}

// ===== 考试详情弹框 =====
// 弹框不滚动（固定大小），表格在 max-height 内部滚动
.gen-att-detail-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.gen-att-detail-ops {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.gen-att-detail-upload {
  ::v-deep .el-upload-list { display: none; }
}

.gen-att-detail-tip {
  font-size: 12px;
  color: #94a3b8;
}

// 表格容器：横向滚动（列很多时），纵向由 el-table 的 max-height 内部滚动
.gen-att-detail-table {
  overflow-x: auto;
}

// ===== 上传卡片 =====
.gen-att-upload-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.gen-att-upload-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #f5f7fa;
  border-bottom: 1px solid #e8ecf1;
  white-space: nowrap;
}

.gen-att-upload-title {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  flex-shrink: 0;
  padding: 4px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 5px;

  > i { color: #d97706; font-size: 15px; flex-shrink: 0; }

  span { white-space: nowrap; }

  // 红色提示小字（括号说明）
  .gen-att-upload-title-tip {
    font-size: 11px;
    font-weight: 400;
    color: #ef4444;
  }
}

.gen-att-tip {
  font-size: 10px;
  color: #94a3b8;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gen-att-upload-body {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;

  // 缩小拖拽区域，用 flex 替代 line-height 居中避免文字溢出
  ::v-deep .el-upload-dragger {
    width: 100%;
    height: 130px;
    line-height: normal;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 6px;

    .el-icon-upload {
      margin: 0;
      font-size: 36px;
      line-height: 36px;
      color: #c0c4cc;
    }

    .el-upload__text {
      font-size: 12px;
      margin-top: 6px;
      color: #606266;
      text-align: center;
      line-height: 1.5;
      padding: 0 16px;

      em { color: #409eff; }
    }
  }
}

.gen-att-upload {
  ::v-deep .el-upload-list { display: none; }
}

.gen-att-upload-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #f59e0b;

  i { animation: rotating 1.5s linear infinite; }
  @keyframes rotating { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
}

.gen-att-upload-status-card {
  margin: 0 16px 14px;
  padding: 12px 14px;
  border: 1px solid rgba(245, 158, 11, 0.22);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 251, 235, 0.96), rgba(255, 255, 255, 0.98));
}

.gen-att-upload-status-card__main {
  color: #b45309;
  font-size: 14px;
  font-weight: 850;
}

.gen-att-upload-status-card__sub {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
}

.is-compact-upload {
  height: 100%;

  .gen-att-upload-card {
    height: 100%;
    border-radius: 16px;
    box-shadow: 0 12px 24px rgba(245, 158, 11, 0.07);
  }

  .gen-att-upload-header {
    background: linear-gradient(135deg, #fffbeb, #ffffff);
  }

  .gen-att-upload-title i { color: #d97706; }

  .gen-att-upload-body {
    padding: 14px 16px;

    ::v-deep .el-upload-dragger {
      height: 150px;
      border-color: rgba(245, 158, 11, 0.42);
      background: linear-gradient(180deg, #ffffff, #fffaf0);

      .el-icon-upload { color: #d97706; }
      .el-upload__text em { color: #d97706; }
    }
  }
}

.is-table-only .gen-att-table-card {
  margin-top: 0;
}

// ===== 整合数据表格卡片 =====
.gen-att-table-card {
  margin-top: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.gen-att-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: #f5f7fa;
  border-bottom: 1px solid #e8ecf1;
}

.gen-att-table-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #047857;
  padding: 4px 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 5px;

  i { color: #059669; font-size: 17px; }
}

.gen-att-table-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #10b981;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
}

.gen-att-table-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.gen-att-table-meta {
  font-size: 12px;
  color: #64748b;
}

.gen-att-table-body {
  min-height: 80px;
  overflow-x: auto;
}

// 单元格内容水平垂直居中
::v-deep .att-cell-center {
  .cell {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// 异常签到单元格（日期列下，非 OK/请假/本人姓名 的值，如代签）标黄
::v-deep .att-cell-warn {
  background-color: #fff3cd !important;
  .cell { color: #b45309; font-weight: 600; }
}

.att-cell-text {
  width: 100%;
  min-height: 28px;
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    color: #2563eb;
    background: rgba(239, 246, 255, 0.86);
  }
}

.att-cell-editing {
  width: 100%;
}

.att-cell-input {
  width: 100%;
}

::v-deep .is-cell-editing .el-table__cell {
  background: #ffffff !important;
}

// 空状态
.gen-att-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 24px 36px;
}

.gen-att-empty-icon {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  i { font-size: 28px; color: #10b981; }
}

.gen-att-empty-text {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.gen-att-empty-hint {
  font-size: 12px;
  color: #94a3b8;
}

// 表格区域
.gen-att-table-wrap {
  overflow-x: auto;
}
</style>

<style lang="scss">
// 考试详情弹框（非 scoped）：弹框本身不滚动，表格在 max-height 内部滚动
.gen-att-detail-dialog {
  .el-dialog__body {
    padding: 16px 20px;
  }
}
</style>
